import React, { useEffect, useRef, useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { http_put } from '../utils/api_client';
import { API_ENDPOINTS } from '../utils/endpoints';
import SectionTree from './SectionTree';
import DocBlocks from './blocks/DocBlocks';
import DocDesignModal from './DocDesignModal';

const genId = () => `sec_${Math.random().toString(36).slice(2, 9)}`;
const slugify = (s) => String(s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const normChild = (s) => ({
  id: s.id || genId(),
  title: s.title || 'Untitled',
  slug: s.slug || slugify(s.title || 'untitled'),
  is_group: !!s.is_group,
  blocks: Array.isArray(s.blocks) ? s.blocks : []
});
const normSection = (s) => ({ ...normChild(s), children: Array.isArray(s.children) ? s.children.map(normChild) : [] });

const swap = (arr, i, j) => {
  if (j < 0 || j >= arr.length) return arr;
  const next = [...arr];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};

const findSection = (sections, id) => {
  for (const s of sections) {
    if (s.id === id) return s;
    const c = (s.children || []).find((x) => x.id === id);
    if (c) return c;
  }
  return null;
};

// BL-117 — Docs editor shell: left rail holds the section tree + the selected
// section's inspector; the document-flow canvas takes the rest of the width so
// the writing area is as large as possible. Debounced autosave throughout.
const DocEditor = ({ slug, companySlug, initial }) => {
  const [name, setName] = useState(initial.name || '');
  const [description, setDescription] = useState(initial.description || '');
  const [sections, setSections] = useState(() => (initial.content?.sections || []).map(normSection));
  const [branding, setBranding] = useState(() => initial.content?.branding || {});
  const [designOpen, setDesignOpen] = useState(false);
  const [activeId, setActiveId] = useState(() => initial.content?.sections?.[0]?.id || null);
  const [saveState, setSaveState] = useState('saved');
  const [publishing, setPublishing] = useState(false);
  const firstRun = useRef(true);

  const active = findSection(sections, activeId);

  const addSection = () => {
    const s = normSection({ title: 'New section' });
    setSections((prev) => [...prev, s]);
    setActiveId(s.id);
  };
  const addSubSection = (parentId) => {
    const c = normChild({ title: 'New subsection' });
    setSections((prev) => prev.map((s) => (s.id === parentId ? { ...s, children: [...s.children, c] } : s)));
    setActiveId(c.id);
  };
  const updateSection = (id, patch) => {
    const apply = (s) => ({ ...s, ...patch, ...(patch.title !== undefined ? { slug: slugify(patch.title) } : {}) });
    setSections((prev) => prev.map((s) => {
      if (s.id === id) return apply(s);
      if ((s.children || []).some((c) => c.id === id)) return { ...s, children: s.children.map((c) => (c.id === id ? apply(c) : c)) };
      return s;
    }));
  };
  const removeSection = (id) => {
    setSections((prev) => prev.filter((s) => s.id !== id).map((s) => ({ ...s, children: (s.children || []).filter((c) => c.id !== id) })));
    if (activeId === id) setActiveId(null);
  };
  const moveSection = (id, dir) => {
    setSections((prev) => {
      const topIdx = prev.findIndex((s) => s.id === id);
      if (topIdx !== -1) return swap(prev, topIdx, topIdx + (dir === 'up' ? -1 : 1));
      return prev.map((s) => {
        const ci = (s.children || []).findIndex((c) => c.id === id);
        if (ci === -1) return s;
        return { ...s, children: swap(s.children, ci, ci + (dir === 'up' ? -1 : 1)) };
      });
    });
  };

  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return undefined; }
    setSaveState('saving');
    const t = setTimeout(() => {
      http_put(API_ENDPOINTS.PUT_DOC(slug), { name, description, content: { sections, branding } })
        .then(() => setSaveState('saved'))
        .catch(() => { setSaveState('saved'); toast.error('Could not save changes'); });
    }, 900);
    return () => clearTimeout(t);
  }, [name, description, sections, branding, slug]);

  const preview = () => { window.location.href = `/${companySlug}/docs/${slug}`; };

  const publish = () => {
    setPublishing(true);
    http_put(API_ENDPOINTS.PUBLISH_DOC(slug), {})
      .then(() => toast.success('Doc published'))
      .catch(() => toast.error('Could not publish'))
      .finally(() => setPublishing(false));
  };

  return (
    <div className="doc-editor">
      <ToastContainer position="bottom-right" autoClose={3000} />

      {designOpen && (
        <DocDesignModal
          branding={branding}
          onChange={setBranding}
          onClose={() => setDesignOpen(false)}
        />
      )}

      <div className="doc-tbar">
        <div className="doc-tbar__bc">Docs / <b>{name || 'Untitled doc'}</b></div>
        <div className="doc-tbar__sp" />
        <span className={`doc-tbar__save ${saveState}`}>{saveState === 'saving' ? 'Saving…' : 'Saved'}</span>
        <button type="button" className="doc-btn doc-btn--white" onClick={() => setDesignOpen(true)}>Design</button>
        <button type="button" className="doc-btn doc-btn--white" onClick={preview}>Preview</button>
        <button type="button" className="doc-btn doc-btn--primary" onClick={publish} disabled={publishing}>
          {publishing ? 'Publishing…' : 'Publish'}
        </button>
      </div>

      <div className="doc-grid">
        <div className="doc-side">
          <SectionTree
            sections={sections}
            activeId={activeId}
            onSelect={setActiveId}
            onAddSection={addSection}
            onAddSub={addSubSection}
            onMove={moveSection}
            onRemove={removeSection}
          />

          <div className="doc-inspector">
            <div className="doc-inspector__h">Section</div>
            {!active ? (
              <div className="doc-inspector__empty">No section selected.</div>
            ) : (
              <div className="doc-inspector__b">
                <label className="doc-field">
                  <span>Title</span>
                  <input value={active.title} onChange={(e) => updateSection(active.id, { title: e.target.value })} />
                </label>
                <div className="doc-toggle" onClick={() => updateSection(active.id, { is_group: !active.is_group })}>
                  <div>
                    <div className="doc-toggle__t">Group heading</div>
                    <div className="doc-toggle__d">A non-clickable header that groups sections, not its own page.</div>
                  </div>
                  <span className={`doc-sw ${active.is_group ? 'on' : ''}`} />
                </div>
                <button type="button" className="doc-remove" onClick={() => removeSection(active.id)}>Remove section</button>
              </div>
            )}
          </div>
        </div>

        <div className="doc-canvas">
          <input
            className="doc-canvas__title"
            value={name}
            placeholder="Untitled doc"
            onChange={(e) => setName(e.target.value)}
          />
          <input
            className="doc-canvas__desc"
            value={description}
            placeholder="Add a short description…"
            onChange={(e) => setDescription(e.target.value)}
          />

          <div className="doc-canvas__divider" />

          {!active && (
            <div className="doc-canvas__empty">
              {sections.length === 0
                ? 'Add a section on the left to start building your doc.'
                : 'Select a section on the left to edit its content.'}
            </div>
          )}

          {active && (
            <div className="doc-canvas__section">
              <div className="doc-canvas__sectionhead">{active.title || 'Untitled section'}</div>
              <DocBlocks
                blocks={active.blocks}
                onChange={(b) => updateSection(active.id, { blocks: b })}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocEditor;
